export const AGENT_DASHBOARD_TOKEN_STORAGE_KEY = 'agent-dashboard-jwt';
export const CLAUDE_CODE_TOKEN_STORAGE_KEY = 'claude-code-jwt';
export const AGENT_DASHBOARD_AUTH_CLEARED_EVENT = 'agent-dashboard-auth-cleared';

/** Mints an organization token for the signed-in member, or answers null when it cannot. */
export type StaffTokenProvider = () => Promise<string | null | undefined>;

// A provider rather than a token: organization tokens expire within the hour, and the identity
// SDK already caches and refreshes one. Holding a copy here would keep sending a dead token.
let staffTokenProvider: StaffTokenProvider | null = null;

export const setStaffTokenProvider = (provider: StaffTokenProvider | null) => {
  staffTokenProvider = provider;
};

// The organization token says what the member may do but never who they are. The identity token
// from sign-in says who, and authorizes nothing — the API re-verifies it before believing a word.
let identityAssertionProvider: StaffTokenProvider | null = null;

export const setIdentityAssertionProvider = (provider: StaffTokenProvider | null) => {
  identityAssertionProvider = provider;
};

/** V2 staff endpoints accept only a live Logto organization token, never a legacy browser token. */
export const getStaffOrganizationToken = async (): Promise<string | null> => {
  if (!staffTokenProvider) {
    return null;
  }
  return (await staffTokenProvider().catch(() => null)) ?? null;
};

export const getIdentityAssertion = async (): Promise<string | null> => {
  if (!identityAssertionProvider) {
    return null;
  }
  return (await identityAssertionProvider().catch(() => null)) ?? null;
};

export const getAgentDashboardToken = (): string | null =>
  localStorage.getItem(AGENT_DASHBOARD_TOKEN_STORAGE_KEY) || localStorage.getItem(CLAUDE_CODE_TOKEN_STORAGE_KEY);

/** What to put on the wire: staff sign-in wins, and the dashboard JWT carries the rest until it retires. */
export const getAuthorizationToken = async (): Promise<string | null> => {
  const staffToken = await getStaffOrganizationToken();
  if (staffToken) {
    return staffToken;
  }
  return getAgentDashboardToken();
};

export const clearAgentDashboardTokens = (options?: { notify?: boolean }) => {
  staffTokenProvider = null;
  identityAssertionProvider = null;
  localStorage.removeItem(AGENT_DASHBOARD_TOKEN_STORAGE_KEY);
  localStorage.removeItem(CLAUDE_CODE_TOKEN_STORAGE_KEY);
  if (options?.notify !== false) {
    window.dispatchEvent(new Event(AGENT_DASHBOARD_AUTH_CLEARED_EVENT));
  }
};
